/**
 * Lista administrativa de cartões: filtros, contagens por tipo e a janela
 * visível. Módulo puro — sem React e sem HTTP.
 *
 * Até o back landar, a lista pagina em memória. Ver
 * openspec/changes/flashcards-revisao-redesign/design.md (Risks: a lista
 * administrativa pagina em memória até o back landar). O ramo é
 * `ReviewCardsPage.serverPaged`: `false` filtra e fatia o catálogo que a
 * leitura já trouxe; `true` usa a página e as contagens que o servidor já
 * resolveu, sem filtrar `items` de novo.
 */
import type { CardLayout, ReviewCardAdminRow, ReviewCardsPage } from '../../../sdk/@types/Revisao';
import { formatShortId } from '../../components/Revisao/cartaoLinha';

export { formatShortId };

export const ADMIN_CARDS_PAGE_SIZE = 12;

export interface AdminListFilters {
  /** Empty matches everything. The resolver trims. */
  q: string;
  layout: CardLayout | 'all';
  /** "" = every matéria. */
  subjectId: string;
  /** "" = every tag. Exact match on one of the card's tags. */
  tag: string;
}

export interface AdminListWindow {
  rows: ReviewCardAdminRow[];
  /** Counts under every current filter EXCEPT layout. Missing layout counts as basic (same rule as ReviewService.typeCountsFromItems: only `single` and `occlusion` are themselves; everything else is basic). The three numbers sum to `announcedTotal`. */
  typeCounts: Record<CardLayout, number>;
  /** basic + single + occlusion. The total the type chips announce. NOT reduced by the layout filter. */
  announcedTotal: number;
  /** How many cards match every filter INCLUDING layout. Denominator of the visible range. */
  matchTotal: number;
  rangeFrom: number;
  rangeTo: number;
  rangeLabel: string;
  serverPaged: boolean;
  /** 1-based. Clamped into 1..pageCount, except a server page past the end:
   *  that read stays on the page that was asked for, with zero rows and the
   *  real total, so it is not rewritten as page 1. */
  page: number;
  pageCount: number;
}

/** Tags distintas dos itens, em `pt`, sem strings vazias e sem inventar tag. */
export function tagOptions(items: readonly { tags?: string[] }[]): string[] {
  const seen = new Set<string>();
  for (const item of items) {
    for (const tag of item.tags ?? []) {
      if (tag !== '') seen.add(tag);
    }
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b, 'pt'));
}

export function resolveAdminList(args: {
  page: ReviewCardsPage;
  filters: AdminListFilters;
  subjectName: (subjectId: string) => string | undefined;
  visiblePage: number;
  pageSize?: number;
}): AdminListWindow {
  const pageSize = resolvePageSize(args.pageSize);
  if (args.page.serverPaged) {
    return resolveServerPaged(args.page, args.visiblePage, pageSize);
  }
  return resolveInMemory(args.page, args.filters, args.subjectName, args.visiblePage, pageSize);
}

function resolvePageSize(pageSize: number | undefined): number {
  if (pageSize !== undefined && Number.isFinite(pageSize) && pageSize >= 1) return pageSize;
  return ADMIN_CARDS_PAGE_SIZE;
}

/**
 * O servidor já filtrou e fatou. Recontar `items` zeraria os tipos que não
 * caíram nesta página, e um filtro de layout zeraria os outros.
 */
function resolveServerPaged(
  page: ReviewCardsPage,
  visiblePage: number,
  pageSize: number,
): AdminListWindow {
  const typeCounts: Record<CardLayout, number> = {
    basic: page.typeCounts.basic,
    single: page.typeCounts.single,
    occlusion: page.typeCounts.occlusion,
  };
  const reported = page.page;
  const chosen = Number.isInteger(reported) && reported >= 1 ? reported : visiblePage;
  const resolved = finish({
    rows: page.items,
    typeCounts,
    matchTotal: page.total,
    page: chosen,
    pageSize,
    serverPaged: true,
  });
  // Página além do fim: a leitura devolve items vazio e o total verdadeiro,
  // não um erro. finish() puxa o número da página para a última que cabe no
  // total; com zero linhas isso pareceria a página 1 vazia e desligaria
  // "Anterior". O número pedido fica, o total continua em matchTotal.
  if (resolved.rows.length === 0 && resolved.matchTotal > 0 && chosen > resolved.pageCount) {
    return { ...resolved, page: chosen };
  }
  return resolved;
}

/**
 * `items` é o catálogo inteiro. Matéria, tag e busca formam a base — o layout
 * fica de fora, para a contagem dos chips não mudar quando um tipo é escolhido.
 */
function resolveInMemory(
  page: ReviewCardsPage,
  filters: AdminListFilters,
  subjectName: (subjectId: string) => string | undefined,
  visiblePage: number,
  pageSize: number,
): AdminListWindow {
  const base = page.items.filter((card) => matchesBase(card, filters, subjectName));
  const typeCounts = typeCountsFromCards(base);
  const layout = filters.layout;
  const matched = layout === 'all' ? base : base.filter((card) => matchesLayout(card, layout));
  const matchTotal = matched.length;
  const pageCount = Math.max(1, Math.ceil(matchTotal / pageSize));
  const safePage = clampPage(visiblePage, pageCount);
  const start = (safePage - 1) * pageSize;
  return finish({
    rows: matched.slice(start, start + pageSize),
    typeCounts,
    matchTotal,
    page: safePage,
    pageSize,
    serverPaged: false,
  });
}

function finish(args: {
  rows: ReviewCardAdminRow[];
  typeCounts: Record<CardLayout, number>;
  matchTotal: number;
  page: number;
  pageSize: number;
  serverPaged: boolean;
}): AdminListWindow {
  const pageCount = Math.max(1, Math.ceil(args.matchTotal / args.pageSize));
  const page = clampPage(args.page, pageCount);
  const empty = args.rows.length === 0;
  const rangeFrom = empty ? 0 : (page - 1) * args.pageSize + 1;
  const rangeTo = empty ? 0 : rangeFrom + args.rows.length - 1;
  return {
    rows: args.rows,
    typeCounts: args.typeCounts,
    announcedTotal: args.typeCounts.basic + args.typeCounts.single + args.typeCounts.occlusion,
    matchTotal: args.matchTotal,
    rangeFrom,
    rangeTo,
    rangeLabel: rangeLabel(args.matchTotal, args.rows.length, rangeFrom, rangeTo),
    serverPaged: args.serverPaged,
    page,
    pageCount,
  };
}

function clampPage(value: number, pageCount: number): number {
  const page = Number.isFinite(value) ? Math.trunc(value) : 1;
  if (page < 1) return 1;
  if (page > pageCount) return pageCount;
  return page;
}

function rangeLabel(matchTotal: number, rowCount: number, rangeFrom: number, rangeTo: number): string {
  if (matchTotal === 0) return 'Nenhum cartão';
  if (rowCount === 0) return `Mostrando 0 de ${matchTotal}`;
  if (rangeFrom === rangeTo) return `Mostrando ${rangeFrom} de ${matchTotal}`;
  return `Mostrando ${rangeFrom}\u2013${rangeTo} de ${matchTotal}`;
}

/** Só `single` e `occlusion` são si mesmos; o resto, inclusive layout ausente, é `basic`. */
function countedLayout(layout: CardLayout | undefined): CardLayout {
  if (layout === 'single' || layout === 'occlusion') return layout;
  return 'basic';
}

function typeCountsFromCards(cards: readonly ReviewCardAdminRow[]): Record<CardLayout, number> {
  const typeCounts: Record<CardLayout, number> = { basic: 0, single: 0, occlusion: 0 };
  for (const card of cards) typeCounts[countedLayout(card.layout)] += 1;
  return typeCounts;
}

function matchesLayout(card: ReviewCardAdminRow, layout: CardLayout): boolean {
  return countedLayout(card.layout) === layout;
}

function matchesBase(
  card: ReviewCardAdminRow,
  filters: AdminListFilters,
  subjectName: (subjectId: string) => string | undefined,
): boolean {
  if (filters.subjectId !== '' && card.subjectId !== filters.subjectId) return false;
  if (filters.tag !== '' && !(card.tags ?? []).includes(filters.tag)) return false;
  return matchesSearch(card, filters.q, subjectName);
}

function matchesSearch(
  card: ReviewCardAdminRow,
  q: string,
  subjectName: (subjectId: string) => string | undefined,
): boolean {
  const query = q.trim();
  if (query === '') return true;
  if (matchesShortId(query, card.shortId)) return true;
  const needle = query.toLowerCase();
  return searchTexts(card, subjectName).some((text) => text.toLowerCase().includes(needle));
}

/**
 * A consulta inteira é um identificador (`#0142`, `0142`, `142`) ou contém o
 * id formatado. A igualdade numérica acha o cartão; um trecho só de dígitos
 * (`14`) também acha `#0142`, porque o trecho cabe no id — o contrário não
 * (`142` não acha o 14). Texto livre não entra aqui.
 */
function matchesShortId(query: string, shortId: number | undefined): boolean {
  if (typeof shortId !== 'number' || !Number.isFinite(shortId)) return false;
  const trimmed = query.trim().toLowerCase();
  const formatted = formatShortId(shortId).toLowerCase();
  if (trimmed.includes(formatted)) return true;
  const stripped = trimmed.startsWith('#') ? trimmed.slice(1).trim() : trimmed;
  if (!/^\d+$/.test(stripped)) return false;
  const id = Math.trunc(shortId);
  if (Number(stripped) === id) return true;
  const digits = formatted.slice(1);
  return digits.includes(stripped) || String(id).includes(stripped);
}

function searchTexts(
  card: ReviewCardAdminRow,
  subjectName: (subjectId: string) => string | undefined,
): string[] {
  const texts: string[] = [];
  if (card.front) texts.push(card.front);
  if (card.back) texts.push(card.back);
  const subject = subjectName(card.subjectId);
  if (subject) texts.push(subject);
  for (const tag of card.tags ?? []) texts.push(tag);
  for (const mask of card.masks ?? []) texts.push(mask.label);
  return texts;
}
